import type { components } from '../../api/generated/schema'

type Schema<Name extends keyof components['schemas']> = components['schemas'][Name]
type DeepNullable<T> = T extends readonly (infer Item)[]
  ? DeepNullable<Item>[]
  : T extends object
    ? { [K in keyof T]: undefined extends T[K] ? DeepNullable<T[K]> | null : DeepNullable<T[K]> }
    : T
type DeepResponse<T> = T extends readonly (infer Item)[]
  ? DeepResponse<Item>[]
  : T extends object
    ? { [K in keyof T]-?: undefined extends T[K] ? DeepResponse<Exclude<T[K], undefined>> | null : DeepResponse<T[K]> }
    : T
type Draft<Name extends keyof components['schemas'], Kind extends OrderType> =
  Omit<Partial<DeepNullable<Schema<Name>>>, 'orderType'> & { orderType: Kind }
type Response<Name extends keyof components['schemas'], Kind extends OrderType> =
  Omit<DeepResponse<Schema<Name>>, 'orderType'> & { orderType: Kind }

export type RoofOrientation = Schema<'RoofFaceDto'>['orientation']
export const ROOF_ORIENTATIONS: RoofOrientation[] = [
  'NORTH', 'NORTH_EAST', 'EAST', 'SOUTH_EAST', 'SOUTH', 'SOUTH_WEST', 'WEST', 'NORTH_WEST', 'FLAT',
]

export type RequestStatus = Schema<'SolarRequestDetailDto'>['status']
export const LISTING_DURATIONS_DAYS = [7, 14, 21, 28] as const
export const PREFERRED_TIME_WINDOWS = [
  '8-10 Uhr', '10-12 Uhr', '12-14 Uhr', '14-16 Uhr', '16-18 Uhr', '18-20 Uhr', '20-22 Uhr',
] as const

export type RoofImageSource = Schema<'PvRequestDetailDto'>['roofImageSource']
export type RoofFace = DeepNullable<Schema<'RoofFaceDto'>>
// Muss mit @DecimalMax auf RoofFaceRequest.usableAreaSqm (backend) übereinstimmen - blockiert
// klare Tippfehler (z.B. eine zusätzliche Null), lässt aber auch großzügige Gewerbedächer zu.
export const MAX_ROOF_FACE_AREA_SQM = 2000
export type RoofType = NonNullable<Schema<'PvRequestDetailDto'>['roofType']>
export const ROOF_TYPES: RoofType[] = [
  'SATTELDACH', 'WALMDACH', 'FLACHDACH', 'PULTDACH', 'KRUEPPELWALMDACH', 'ZELTDACH',
  'MANSARDENDACH', 'SONSTIGES',
]
export type RoofMaterial = NonNullable<Schema<'PvRequestDetailDto'>['roofMaterial']>
export const ROOF_MATERIALS: RoofMaterial[] = [
  'ZIEGELDACH', 'DACHPAPPE', 'TRAPEZDACH', 'WELLDACH', 'BIBERSCHWANZ', 'SCHIEFERDACH',
  'KIESDACH', 'STEHFALZDACH', 'GRUENDACH', 'KUNSTDACH', 'ASBEST', 'UNSICHER',
]
export type HouseType = NonNullable<Schema<'SolarRequestDetailDto'>['houseType']>
export const HOUSE_TYPES: HouseType[] = [
  'EINFAMILIENHAUS', 'DOPPELHAUSHAELFTE', 'REIHENHAUS', 'MEHRFAMILIENHAUS', 'GEWERBE',
]
export type ConsumptionTimePattern = NonNullable<Schema<'CompleteSystemDraftRequest'>['consumptionTimePattern']>
export const CONSUMPTION_TIME_PATTERNS: ConsumptionTimePattern[] = ['MORGENS_ABENDS', 'GANZTAGS']
export type OrderType = Schema<'SolarRequestDetailDto'>['orderType']
export const ORDER_TYPES: OrderType[] = [
  'KOMPLETTSYSTEM_MIT_MONTAGE', 'DACHMONTAGE', 'ELEKTROINSTALLATION', 'WAERMEPUMPE', 'BERATUNG',
  'ELEKTROABNAHME_ANMELDUNG',
]
export const CONSULTATION_INTERESTED_ORDER_TYPES: OrderType[] = ORDER_TYPES.filter(type => type !== 'BERATUNG')
export type CurrentHeatingType = NonNullable<Schema<'HeatPumpDraftRequest'>['currentHeatingType']>
export const CURRENT_HEATING_TYPES: CurrentHeatingType[] = [
  'GAS', 'OEL', 'NACHTSPEICHER', 'FERNWAERME', 'WAERMEPUMPE_VORHANDEN', 'SONSTIGES',
]
export type HeatDistribution = NonNullable<Schema<'HeatPumpDraftRequest'>['heatDistribution']>
export const HEAT_DISTRIBUTIONS: HeatDistribution[] = ['HEIZKOERPER', 'FUSSBODENHEIZUNG', 'GEMISCHT']
export type InsulationStandard = NonNullable<Schema<'HeatPumpDraftRequest'>['insulationStandard']>
export const INSULATION_STANDARDS: InsulationStandard[] = ['UNGEDAEMMT', 'TEILSANIERT', 'VOLLSANIERT']
export type DesiredHeatPumpType = NonNullable<Schema<'HeatPumpDraftRequest'>['desiredHeatPumpType']>
export const DESIRED_HEAT_PUMP_TYPES: DesiredHeatPumpType[] = ['LUFT_WASSER', 'SOLE_WASSER', 'WASSER_WASSER', 'WEISS_NICHT']
export type Country = NonNullable<Schema<'SolarRequestDetailDto'>['country']>
export const COUNTRIES: Country[] = ['DE', 'ES', 'PL', 'AT']
export type MaterialSourcing = NonNullable<Schema<'RoofMountingDraftRequest'>['materialSourcing']>
export const MATERIAL_SOURCING_OPTIONS: MaterialSourcing[] = ['CUSTOMER_PROVIDED', 'CRAFTSMAN_PROVIDED']
export type IndoorOutdoor = NonNullable<Schema<'ElectricalWorkDraftRequest'>['installationLocation']>
export const INDOOR_OUTDOOR_OPTIONS: IndoorOutdoor[] = ['INDOOR', 'OUTDOOR']
export type BatteryPreference = Schema<'CompleteSystemDraftRequest'>['batteryPreference']
export const BATTERY_PREFERENCE_OPTIONS: BatteryPreference[] = ['NO_PREFERENCE', 'WITH_BATTERY', 'WITHOUT_BATTERY']
export type PriceLevel = Schema<'EconomicsEstimateDto'>['pvPriceLevel']
export const PRICE_LEVELS: PriceLevel[] = ['VERY_LOW', 'LOW', 'MIDDLE', 'MUCH', 'TOO_MUCH']

export type SizingRecommendationDto = DeepResponse<Schema<'SizingRecommendationDto'>>
export type EconomicsEstimateDto = DeepResponse<Schema<'EconomicsEstimateDto'>>
export type MonthlyEnergyDto = Schema<'MonthlyEnergyDto'>
export type SizingPreviewDto = DeepResponse<Schema<'SizingPreviewDto'>>

export type CompleteSystemSummaryDto = Response<'CompleteSystemSummaryDto', 'KOMPLETTSYSTEM_MIT_MONTAGE'>
export type ElectricalInstallationSummaryDto = Response<'ElectricalInstallationSummaryDto', 'ELEKTROINSTALLATION'>
export type RoofMountingSummaryDto = Response<'RoofMountingSummaryDto', 'DACHMONTAGE'>
export type HeatPumpSummaryDto = Response<'HeatPumpSummaryDto', 'WAERMEPUMPE'>
export type ConsultationSummaryDto = Response<'ConsultationSummaryDto', 'BERATUNG'>
export type ElectricalAcceptanceSummaryDto = Response<'ElectricalAcceptanceSummaryDto', 'ELEKTROABNAHME_ANMELDUNG'>
export type SolarRequestSummaryDto = CompleteSystemSummaryDto | ElectricalInstallationSummaryDto |
  RoofMountingSummaryDto | HeatPumpSummaryDto | ConsultationSummaryDto | ElectricalAcceptanceSummaryDto

export function isElectricalWorkSummary(request: SolarRequestSummaryDto):
request is CompleteSystemSummaryDto | ElectricalInstallationSummaryDto {
  return request.orderType === 'KOMPLETTSYSTEM_MIT_MONTAGE' || request.orderType === 'ELEKTROINSTALLATION'
}
export function hasManufacturerPreferenceSummary(request: SolarRequestSummaryDto):
request is CompleteSystemSummaryDto | ElectricalInstallationSummaryDto | HeatPumpSummaryDto {
  return isElectricalWorkSummary(request) || request.orderType === 'WAERMEPUMPE'
}
export function isPvRequestSummary(request: SolarRequestSummaryDto):
request is CompleteSystemSummaryDto | ElectricalInstallationSummaryDto | RoofMountingSummaryDto {
  return request.orderType !== 'WAERMEPUMPE' && request.orderType !== 'BERATUNG' &&
    request.orderType !== 'ELEKTROABNAHME_ANMELDUNG'
}

export type CompleteSystemDetailDto = Response<'CompleteSystemDetailDto', 'KOMPLETTSYSTEM_MIT_MONTAGE'>
export type ElectricalInstallationDetailDto = Response<'ElectricalInstallationDetailDto', 'ELEKTROINSTALLATION'>
export type RoofMountingDetailDto = Response<'RoofMountingDetailDto', 'DACHMONTAGE'>
export type HeatPumpDetailDto = Response<'HeatPumpDetailDto', 'WAERMEPUMPE'>
export type ConsultationDetailDto = Response<'ConsultationDetailDto', 'BERATUNG'>
export type ElectricalAcceptanceDetailDto = Response<'ElectricalAcceptanceDetailDto', 'ELEKTROABNAHME_ANMELDUNG'>
export type SolarRequestDetailDto = CompleteSystemDetailDto | ElectricalInstallationDetailDto |
  RoofMountingDetailDto | HeatPumpDetailDto | ConsultationDetailDto | ElectricalAcceptanceDetailDto

export function isElectricalWorkDetail(request: SolarRequestDetailDto):
request is CompleteSystemDetailDto | ElectricalInstallationDetailDto {
  return request.orderType === 'KOMPLETTSYSTEM_MIT_MONTAGE' || request.orderType === 'ELEKTROINSTALLATION'
}
export function hasManufacturerPreferenceDetail(request: SolarRequestDetailDto):
request is CompleteSystemDetailDto | ElectricalInstallationDetailDto | HeatPumpDetailDto {
  return isElectricalWorkDetail(request) || request.orderType === 'WAERMEPUMPE'
}
export function isPvRequestDetail(request: SolarRequestDetailDto):
request is CompleteSystemDetailDto | ElectricalInstallationDetailDto | RoofMountingDetailDto {
  return request.orderType !== 'WAERMEPUMPE' && request.orderType !== 'BERATUNG' &&
    request.orderType !== 'ELEKTROABNAHME_ANMELDUNG'
}

export type CompleteSystemDraftRequest = Draft<'CompleteSystemDraftRequest', 'KOMPLETTSYSTEM_MIT_MONTAGE'>
export type ElectricalInstallationDraftRequest = Draft<'ElectricalInstallationDraftRequest', 'ELEKTROINSTALLATION'>
export type RoofMountingDraftRequest = Draft<'RoofMountingDraftRequest', 'DACHMONTAGE'>
export type HeatPumpDraftRequest = Draft<'HeatPumpDraftRequest', 'WAERMEPUMPE'>
export type ConsultationDraftRequest = Draft<'ConsultationDraftRequest', 'BERATUNG'>
export type ElectricalAcceptanceDraftRequest = Draft<'ElectricalAcceptanceDraftRequest', 'ELEKTROABNAHME_ANMELDUNG'>
export type SolarRequestDraftRequest = CompleteSystemDraftRequest | ElectricalInstallationDraftRequest |
  RoofMountingDraftRequest | HeatPumpDraftRequest | ConsultationDraftRequest | ElectricalAcceptanceDraftRequest

export type AcceptOfferRequest = Schema<'AcceptOfferRequest'>
export type RepublishRequest = DeepNullable<Schema<'RepublishRequest'>>
export type PublishRequest = DeepNullable<Schema<'PublishRequest'>>
